<template>
  <div>
    <h1 class="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">คำสั่งซื้อ</h1>
    
    <!-- Pending Verification Alert -->
    <div v-if="pendingCount > 0" class="mb-4 bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-orange-700">
            <span class="font-bold">มี {{ pendingCount }} รายการ</span> รอตรวจสอบการชำระเงิน
          </p>
        </div>
        <div class="ml-auto">
          <button @click="filterByPending" class="text-sm font-medium text-orange-700 hover:text-orange-600 underline">
            ดูรายการ
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Tabs -->
    <div class="flex gap-2 mb-4 overflow-x-auto pb-2">
      <button 
        @click="filterStatus = ''" 
        class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="filterStatus === '' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
      >
        ทั้งหมด
      </button>
      <button 
        @click="filterStatus = 'pending_verification'" 
        class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="filterStatus === 'pending_verification' ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'"
      >
        รอตรวจสอบ
        <span v-if="pendingCount > 0" class="ml-1 bg-white text-orange-600 px-1.5 py-0.5 rounded-full text-xs">{{ pendingCount }}</span>
      </button>
      <button 
        @click="filterStatus = 'pending_payment'" 
        class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="filterStatus === 'pending_payment' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'"
      >
        รอชำระเงิน
      </button>
      <button 
        @click="filterStatus = 'paid'" 
        class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="filterStatus === 'paid' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'"
      >
        ชำระแล้ว
      </button>
      <button 
        @click="filterStatus = 'shipped'" 
        class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="filterStatus === 'shipped' ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'"
      >
        จัดส่งแล้ว
      </button>
    </div>
    
    <!-- Mobile Card View -->
    <div class="block sm:hidden space-y-4">
      <div v-for="order in orders" :key="order.id" class="bg-white rounded-lg shadow p-4">
        <div class="flex justify-between items-start mb-3">
          <div>
            <p class="font-bold text-gray-900">{{ order.order_number }}</p>
            <p class="text-sm text-gray-500">{{ order.customer_name }}</p>
          </div>
          <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="getStatusClass(order.status)">
            {{ getStatusText(order.status) }}
          </span>
        </div>
        <div class="flex justify-between items-center text-sm">
          <span class="text-gray-600">{{ order.total_amount }}</span>
          <span class="text-gray-500">{{ new Date(order.created_at).toLocaleDateString('th-TH') }}</span>
        </div>
        <router-link :to="`/admin/orders/${order.id}`" class="mt-3 block text-center text-primary hover:text-primary-dark text-sm font-medium py-2 border border-primary rounded">
          ดูรายละเอียด
        </router-link>
      </div>
    </div>

    <!-- Desktop Table View -->
    <div class="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">หมายเลขคำสั่งซื้อ</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ลูกค้า</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ยอดรวม</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">วันที่</th>
              <th class="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="order in orders" :key="order.id">
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ order.order_number }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ order.customer_name }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ order.total_amount }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="getStatusClass(order.status)">
                  {{ getStatusText(order.status) }}
                </span>
              </td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">{{ new Date(order.created_at).toLocaleDateString('th-TH') }}</td>
              <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                <router-link :to="`/admin/orders/${order.id}`" class="text-primary hover:text-primary-dark">ดูรายละเอียด</router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Pagination 
        :current-page="pagination.current_page" 
        :last-page="pagination.last_page" 
        @page-change="fetchOrders" 
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import api from '../../api';
import { useNotification } from '../../composables/useNotification';
import Pagination from '../../components/Pagination.vue';
import { getOrderStatusLabel } from '../../utils/orderStatus';

const orders = ref([]);
const pagination = ref({ current_page: 1, last_page: 1 });
const pendingCount = ref(0);
const filterStatus = ref('');
const { showError, handleApiError } = useNotification();

onMounted(async () => {
    await fetchPendingCount();
    fetchOrders();
});

// Watch filter changes
watch(filterStatus, () => {
    fetchOrders(1);
});

const fetchPendingCount = async () => {
    try {
        const response = await api.get('/admin/orders?status=pending_verification&per_page=1');
        pendingCount.value = response.data.data.total || 0;
    } catch (error) {
        console.error('Failed to fetch pending count');
    }
};

const filterByPending = () => {
    filterStatus.value = 'pending_verification';
};

const fetchOrders = async (page = 1) => {
    try {
        let url = `/admin/orders?page=${page}`;
        if (filterStatus.value) {
            url += `&status=${filterStatus.value}`;
        }
        const response = await api.get(url);
        orders.value = response.data.data.data;
        pagination.value = {
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page
        };
    } catch (error) {
        handleApiError(error);
    }
};

const getStatusClass = (status) => {
    switch (status) {
        case 'pending_payment': return 'bg-yellow-100 text-yellow-800';
        case 'pending_verification': return 'bg-cream-dark text-primary-dark border border-primary-light';
        case 'paid': return 'bg-green-100 text-green-800';
        case 'processing': return 'bg-blue-100 text-blue-800';
        case 'shipped': return 'bg-purple-100 text-purple-800';
        case 'delivered': return 'bg-emerald-100 text-emerald-800';
        case 'cancelled': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
    }
};

const getStatusText = (status) => getOrderStatusLabel(status);
</script>
